import { deepEqual } from '@direct-frontend/stdlib';

const PARAMS_COMPARING_MAX_DEPTH = 20;

/** Функция подготовки данных чанка перед сборкой строк */
type TransformChunk<ChunkData extends object, QueryArg, TransformedChunk> = (
    chunkData: ChunkData,
    queryArg: QueryArg
) => TransformedChunk;

/**
 * Кеширует результат трансформации чанка, чтобы она выполнялась один раз на чанк, а не на каждую строку.
 *
 * Кеш держится по ссылке на данные чанка — в кеше rtk-query она стабильна, пока чанк не перезапрошен.
 * queryArg сверяется сперва по ссылке: обертка вызывается на каждую строку, в пределах одного прохода
 * по чанку это один и тот же объект, а deepEqual аллоцирует WeakMap еще до сравнения.
 */
export function memoizeTransformChunk<ChunkData extends object, QueryArg, TransformedChunk>(
    transformChunk: TransformChunk<ChunkData, QueryArg, TransformedChunk>
): TransformChunk<ChunkData, QueryArg, TransformedChunk> {
    const cache = new WeakMap<ChunkData, { queryArg: QueryArg; result: TransformedChunk }>();

    return (chunkData, queryArg) => {
        const cached = cache.get(chunkData);
        if (
            cached &&
            (cached.queryArg === queryArg || deepEqual(cached.queryArg, queryArg, PARAMS_COMPARING_MAX_DEPTH))
        ) {
            return cached.result;
        }

        const result = transformChunk(chunkData, queryArg);
        cache.set(chunkData, { queryArg, result });

        return result;
    };
}
